import React, { useEffect, useState } from 'react';
import { X, Check, AlertCircle } from 'lucide-react';
import type { LogoUploadModalProps } from './types';
import { useLogoData } from './useLogoData';
import { useLogoUpload } from './useLogoUpload';
import { TabNavigation } from './TabNavigation';
import { TextConfiguration } from './TextConfiguration';
import { ImageConfiguration } from './ImageConfiguration';
import { LinkSettings } from './LinkSettings';
import { LogoPreview } from './LogoPreview';
import { LegacyBothNotice } from './LegacyBothNotice';
import {
  prepareLogoForSave,
  isLegacyBothLogo,
  hasSeenLegacyBothNotice,
  markLegacyBothNoticeSeen,
} from '../../utils/navbarLogo';

export const LogoUploadModal: React.FC<LogoUploadModalProps> = ({
  isOpen,
  onClose,
  onSave,
  siteId,
  initialData,
  isLoading = false,
  pages = [],
  isLoadingPages = false,
}) => {
  const { logoData, activeTab, setActiveTab, updateLogoData, clearImage, hasImage } =
    useLogoData(initialData);
  const { isUploading, uploadError, fileInputRef, handleFileSelect, setUploadError } =
    useLogoUpload();

  const isLegacyBoth = isLegacyBothLogo(initialData);
  const [showLegacyNotice, setShowLegacyNotice] = useState(false);

  useEffect(() => {
    if (!isOpen || !isLegacyBoth || !siteId) {
      setShowLegacyNotice(false);
      return;
    }
    setShowLegacyNotice(!hasSeenLegacyBothNotice(siteId));
  }, [isOpen, isLegacyBoth, siteId]);

  const dismissLegacyNotice = () => {
    if (siteId) markLegacyBothNoticeSeen(siteId);
    setShowLegacyNotice(false);
  };

  const handleSave = () => {
    if (activeTab === 'text' && !logoData.text?.trim()) {
      setUploadError('Please enter text for the logo.');
      return;
    }

    if (activeTab === 'image' && !logoData.imageUrl && !logoData.cloudfrontUrl) {
      setUploadError('Please upload an image for the logo.');
      return;
    }

    if (isLegacyBoth && siteId) {
      markLegacyBothNoticeSeen(siteId);
      setShowLegacyNotice(false);
    }

    onSave(prepareLogoForSave(logoData, activeTab));
  };

  const onFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(event, siteId, logoData, updates => {
      updateLogoData(updates);
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl border border-gray-100 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Logo Configuration</h2>
            <p className="text-sm text-gray-600 mt-1">
              Choose text or an image for your navigation brand
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
            disabled={isLoading || isUploading}
          >
            <X size={20} />
          </button>
        </div>

        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {showLegacyNotice && <LegacyBothNotice onDismiss={dismissLegacyNotice} />}

            {uploadError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="text-red-700 flex-1">
                  <p className="font-medium">Error</p>
                  <p className="text-sm">{uploadError}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                {activeTab === 'text' && (
                  <TextConfiguration logoData={logoData} onChange={updateLogoData} />
                )}

                {activeTab === 'image' && (
                  <ImageConfiguration
                    logoData={logoData}
                    onChange={updateLogoData}
                    onFileSelect={onFileSelect}
                    onClearImage={clearImage}
                    isUploading={isUploading}
                    hasImage={hasImage}
                    fileInputRef={fileInputRef}
                  />
                )}

                <LinkSettings
                  logoData={logoData}
                  onChange={updateLogoData}
                  pages={pages}
                  isLoadingPages={isLoadingPages}
                />
              </div>

              <LogoPreview logoData={logoData} activeTab={activeTab} hasImage={hasImage} />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
            disabled={isLoading || isUploading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || isUploading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isLoading || isUploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                <span>{isUploading ? 'Uploading...' : 'Saving...'}</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                <span>Save Logo</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogoUploadModal;
